import { inject, Injectable } from '@angular/core';
import { MemberAuthenticationConfiguration, Settings } from '../_models/settings';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import * as deeplink from '@app/shared/utils/browser-deeplink';
import { Meta } from '@angular/platform-browser';
import { LOYALTY_CLUB_METADATA_SELECTOR } from '@app/core/consts';
import { BootstrapLocaleService } from '@app/core/settings/language/bootstrap-locale.service';
import { LanguageSettingsService } from '@app/core/settings/language/language-settings.service';

interface Contact {
  type: 'email' | 'phone';
  value: string;
}

@Injectable()
export class SettingsService {
  private router = inject(Router);
  private boostrapLocaleService = inject(BootstrapLocaleService);
  private languageSettingsService = inject(LanguageSettingsService);

  public readonly settings = this.getSettingsFromMetaTag();

  public getMinimumMemberAge(): number {
    return this.settings.minimum_member_age;
  }

  public getSuggestedMsisdnCountry(): string | null {
    return this.settings.suggestedMsisdnCountry();
  }

  public getAvailableMsisdnCountryCallingCodes(): string[] {
    return this.settings.getAvailableMsisdnCountryCodes();
  }

  public getMemberAuthenticationConfiguration(): MemberAuthenticationConfiguration | null {
    return this.settings.member_authentication_configuration;
  }

  public getTransactionsCurrency(): string | null {
    return this.settings.transactions_currency;
  }

  public getHasAdditionalIndexHtml(): boolean {
    return !!this.settings.additional_index_html;
  }

  public getContact(): Contact | null {
    if (this.settings.contact_email) {
      return { type: 'email', value: this.settings.contact_email };
    } else if (this.settings.contact_phone) {
      return { type: 'phone', value: this.settings.contact_phone };
    }

    return null;
  }

  public isGaleriaDominikanska(): boolean {
    return this.settings.slug === 'galeria-dominikanska';
  }

  public getGamesEmbedTokenParamName(): 'embedToken' | 'token' {
    return this.settings.isHybridLoginFlow() ? 'embedToken' : 'token';
  }

  private getSettingsFromMetaTag(): Settings {
    const rawSettings = inject(Meta).getTag(LOYALTY_CLUB_METADATA_SELECTOR)?.dataset?.settings;
    if (!rawSettings) {
      throw new Error('No club settings found in meta tag');
    }
    const settings = new Settings(JSON.parse(rawSettings));
    this.handleSettingsCallbacks(settings);
    return settings;
  }

  private handleSettingsCallbacks(settings: Settings): void {
    this.setupHtmlTags(settings.custom_js);
    this.setLanguage(settings.locale);
    this.handleMobileApp(settings);
  }

  private setLanguage(locale: string): void {
    this.boostrapLocaleService.init();
    this.languageSettingsService.storeLanguage(locale, false);
  }

  /**
   * Special hack for running custom JS. DO NOT EVER ADD CUSTOM JS BOX. TRUST ME
   */
  private trackEventsAndInjectCustomJs(customJs: string): void {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(
      () => this.evalCustomJS(customJs),
    );
    this.evalCustomJS(customJs);
  }

  private setupHtmlTags(customJS: string): void {
    if (customJS) { this.trackEventsAndInjectCustomJs(customJS); }
  }

  private handleMobileApp(settings: Settings): void {
    if (!settings.mobile_app_setting) { return; }

    deeplink.setup({
      android: {
        appId: settings.mobile_app_setting.android_app_id,
        storeUrl: settings.androidStoreLink(),
      },
      iOS: {
        appId: settings.mobile_app_setting.ios_app_id,
        storeUrl: settings.iosStoreLink(),
      },
    });
  }

  private evalCustomJS(customJS: string): void {
    try {
      eval(customJS);
    } catch (e) {
      console.error('Error while executing', e);
    }
  }
}
